import { Helmet } from "react-helmet-async";
import { sanitizeHtml } from "@/lib/sanitize";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, Calendar } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";

const ProjetDetail = () => {
  const { slug } = useParams<{ slug: string }>();

  const { data: project, isLoading } = useQuery({
    queryKey: ["project", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("*")
        .eq("slug", slug)
        .eq("published", true)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!slug,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Chargement...</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Projet introuvable</h1>
          <Link to="/projets" className="text-primary hover:underline">
            Retour aux projets
          </Link>
        </div>
      </div>
    );
  }

  const images = (project.images as string[]) || [];

  return (
    <>
      <Helmet>
        <title>{project.title} - Projets - Agroref</title>
        <meta name="description" content={project.description || project.title} />
      </Helmet>

      <div className="-mx-4 -mt-16">
        {/* Hero */}
        <section className="bg-[#2c2c2c] text-white py-12 md:py-16 md:py-20 relative">
          <div className="container mx-auto px-4">
            <div className="text-center mb-6">
              <h1 className="text-3xl md:text-5xl font-bold mb-4">{project.title}</h1>
              <div className="w-32 md:w-48 h-0.5 bg-accent mx-auto mb-6"></div>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 -mb-px">
            <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto" preserveAspectRatio="none">
              <path d="M0 0C240 53.3333 480 80 720 80C960 80 1200 53.3333 1440 0V80H0V0Z" fill="white"/>
            </svg>
          </div>
        </section>

        <section className="bg-white py-12 md:py-16">
          <div className="container mx-auto px-4 max-w-4xl">
            <Link to="/projets" className="inline-flex items-center gap-2 text-primary hover:underline mb-8">
              <ArrowLeft className="h-4 w-4" />
              Retour aux projets
            </Link>

            {project.description && (
              <ScrollReveal>
                <p className="text-lg text-muted-foreground mb-8">{project.description}</p>
              </ScrollReveal>
            )}

            {/* Images */}
            {images.length > 0 && (
              <ScrollReveal>
                <div className="grid gap-4 mb-8">
                  {images.map((img, i) => (
                    <img key={i} src={img} alt={`${project.title} - ${i + 1}`} className="w-full rounded-lg" />
                  ))}
                </div>
              </ScrollReveal>
            )}

            {/* Video */}
            {project.video_url && (
              <ScrollReveal>
                <div className="mb-8">
                  <video autoPlay muted loop playsInline className="w-full rounded-lg" preload="auto">
                    <source src={project.video_url} />
                  </video>
                </div>
              </ScrollReveal>
            )}

            {/* Content */}
            <ScrollReveal>
              <div
                className="prose prose-lg max-w-none"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(project.content) }}
              />
            </ScrollReveal>
          </div>
        </section>
      </div>
    </>
  );
};

export default ProjetDetail;
